import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { Flex } from "@redotech/redo-web/flex";
import { MoneyText } from "@redotech/redo-web/fx/money-text";
import { toMoney } from "@redotech/redo-web/fx/to-money";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { OrderFulfillmentAddress } from "../../../common/order-fulfillment-address";
import * as styles from "./outbound-order.module.css";
import {
  RedoContentCard,
  RedoContentCardReorderProps,
} from "./redo-content-card";

export const PaymentInformationCard = memo(function PaymentInformationCard({
  fulfillmentGroup,
  reorderHandleProps,
  showReorderHandle,
}: { fulfillmentGroup: FulfillmentGroupData } & RedoContentCardReorderProps) {
  const totalTax = Number(fulfillmentGroup.totalTax.amount);
  const totalItemsPrice = Number(fulfillmentGroup.totalPrice.amount);
  const totalDiscounts =
    Number(fulfillmentGroup.totalPrice.amount) -
    Number(
      fulfillmentGroup.totalDiscountedPrice?.amount ??
        fulfillmentGroup.totalPrice.amount,
    );
  const customerShippingPaid = Number(
    fulfillmentGroup.customerShippingPaid ?? 0,
  );
  const totalOrderPrice =
    totalItemsPrice + totalTax + customerShippingPaid - totalDiscounts;
  const landedCost = fulfillmentGroup.landedCost;
  const landedCostTotal = landedCost
    ? landedCost.duties + landedCost.taxes + landedCost.fees
    : 0;
  const shippingExcludingLandedCost = Math.max(
    0,
    customerShippingPaid - landedCostTotal,
  );

  const currency = fulfillmentGroup.totalPrice.currency;
  const billingAddress = fulfillmentGroup.billingAddress;

  return (
    <RedoContentCard
      reorderHandleProps={reorderHandleProps}
      showReorderHandle={showReorderHandle}
      title="Payment information"
    >
      <div className={styles.paymentInformationGrid}>
        {billingAddress && (
          <Flex flexDirection="column" gap="lg" minW="none">
            <OrderFulfillmentAddress
              displayAddress={billingAddress}
              fontWeight="medium"
              hideEditButton
              originalAddress={billingAddress}
              titleText="Billing address"
            />
          </Flex>
        )}
        <Flex
          className={styles.paymentSummaryColumn}
          flexDirection="column"
          gap="xxs"
          minW="none"
        >
          <PaymentSummaryRow
            amount={totalItemsPrice}
            currency={currency}
            label="Products"
          />
          {totalDiscounts > 0 && (
            <PaymentSummaryRow
              amount={totalDiscounts}
              currency={currency}
              label="Discounts"
              prefix="-"
            />
          )}
          <PaymentSummaryRow
            amount={totalTax}
            currency={currency}
            label="Estimated tax"
          />
          <PaymentSummaryRow
            amount={shippingExcludingLandedCost}
            currency={currency}
            label="Shipping"
          />
          {landedCost && (
            <>
              <PaymentSummaryRow
                amount={landedCost.duties}
                currency={currency}
                label="Duties"
              />
              <PaymentSummaryRow
                amount={landedCost.taxes + landedCost.fees}
                currency={currency}
                label="Taxes and fees"
              />
            </>
          )}
          <Divider />
          <PaymentSummaryRow
            amount={totalOrderPrice}
            currency={currency}
            label="Total"
            prominent
          />
        </Flex>
      </div>
    </RedoContentCard>
  );
});

const PaymentSummaryRow = memo(function PaymentSummaryRow({
  amount,
  currency,
  label,
  prefix,
  prominent = false,
}: {
  amount: number;
  currency: string;
  label: string;
  prefix?: string;
  prominent?: boolean;
}) {
  return (
    <Flex align="center" gap="xl" justify="space-between" minW="none">
      <Text
        color={prominent ? "primary" : "tertiary"}
        fontSize="xs"
        fontWeight="medium"
      >
        {label}
      </Text>
      <Text
        color={prominent ? "primary" : "secondary"}
        fontSize="xs"
        fontWeight="medium"
      >
        {prefix}
        <MoneyText struck value={toMoney({ amount, currency })} />
      </Text>
    </Flex>
  );
});
